import * as Immutable from 'immutable';
import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';

import analytics from '../../shared/services/analytics/analytics';
import { StateType } from '../../reducers';
import { alphasOptionsGradeChoicesSelector } from '../../reducers/AlphasReducer/AlphasSelector';
import { getUserIdFromUserSelector, getUserPermissionsSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { formatHTMLMessageHelper, FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { addModifiers } from '../../shared/helpers';
import { getFormattedMatch } from '../AlphaTestingStatus/TestingStatus/SummaryCheckData';
import { ToolTip } from '../ToolTip';
import { isTestPeriodControlShownSelector, isTestPeriodShownSelector } from '../../reducers/EditorReducer/EditorSelector';
import { TypeStage } from '../AlphaSummary/Summary/types';
import { Classification, PyramidMultiplier, Theme } from '../../shared/api/api';

interface HOCStageChooserProps {
  path: string[];
  modifiers?: string[];
  title: string;
  renderGrade?: boolean;
  renderTheme?: boolean;
  renderClassification?: boolean;
  renderPyramids?: boolean;
  id?: string;
  isStageChooserHidden?: boolean;
}

interface HOCStageChooserStateProps {
  currentStage: string;
  alphaId: string;
  alphaIs: string;
  alphaOs: string;
  alphaProd: string;
  permissions: Immutable.List<string>;
  alphaGrade: string;
  themes?: Immutable.List<Theme>;
  classifications?: Immutable.List<Classification>;
  pyramidThemes?: Immutable.Record<PyramidThemes>;
  fetchingCheck: boolean;
  fetchingSubmissionAlpha: boolean;
  alpha: any;
  userId: string;
  gradeOptions: Immutable.List<any>;
  isTestPeriodControlShown: boolean;
  isTestPeriodShown: boolean;
}

interface StatusProps {
  grade: string;
  gradeOptions: Immutable.List<any>;
}

interface PyramidThemes {
  effective: number;
  pyramids: PyramidMultiplier[];
}

const Status = ({ grade, gradeOptions }: StatusProps) => {
  if (grade == null) {
    return <></>;
  }
  const gradeOption = gradeOptions && gradeOptions.find((option: any) => option.get('value') === grade);
  const gradeLabel = gradeOption && gradeOption.get('label');

  if (!gradeOption) {
    return <></>;
  }

  return (
    <div className="summary__status">
      <div className="summary__status-button">
        <div className={addModifiers('summary__status-button-icon', [grade.toLowerCase()])} />
        {gradeLabel}
      </div>
    </div>
  );
};

interface ThemeLabelProps {
  theme: Theme;
}

export const AlphaStatusThemeLabel = ({ theme, intl }: ThemeLabelProps & InjectedIntlProps) => {
  const formattedMatch = getFormattedMatch(theme);

  return (
    <div className="summary__status">
      <div className={addModifiers('summary__status-button', ['theme'])}>
        <div className={addModifiers('summary__status-button-icon', ['theme'])} />
        <div className={'summary__status-theme-text'}> {formatHTMLMessageHelper(intl, 'themeLabel.match', formattedMatch)}</div>
        <ToolTip
          label="themeLabel.hintTitle"
          tooltipInfo={formatHTMLMessageHelper(intl, 'statusMessageIS.MATCHES_THEMES.PASS.single', formattedMatch)}
          isTooltipInfoFormatted={true}
          btnModifiers={['theme']}
        />
      </div>
    </div>
  );
};

interface ClassificationLabelProps {
  classification: Classification;
}

export const AlphaStatusClassificationLabel = ({ classification, intl }: ClassificationLabelProps & InjectedIntlProps) => {
  return (
    <div className="summary__status">
      <div className={addModifiers('summary__status-button', ['classification'])}>
        <div className={addModifiers('summary__status-button-icon', ['classification'])} />
        <div className={'summary__status-classification-text'}> {classification.name}</div>
        <ToolTip
          label="classificationLabel.hintTitle"
          tooltipInfo={formatHTMLMessageHelper(intl, 'statusMessageIS.MATCHES_CLASSIFICATION.PASS.single', { value: classification.name })}
          isTooltipInfoFormatted={true}
          btnModifiers={['theme']}
        />
      </div>
    </div>
  );
};

interface PyramidLabelProps {
  pyramid: PyramidMultiplier;
  effective?: number;
}

export const AlphaStatusPyramidLabel = ({ pyramid, effective, intl }: PyramidLabelProps & InjectedIntlProps) => {
  const formattedMatch = getFormattedMatch(pyramid);
  const enrichedStats = { effective, ...formattedMatch };

  return (
    <div className="summary__status">
      <div className={addModifiers('summary__status-button', ['pyramid'])}>
        <div className={addModifiers('summary__status-button-icon', ['pyramid'])} />
        <div className={'summary__status-classification-text'}>{formatHTMLMessageHelper(intl, 'pyramidLabel.match', enrichedStats)}</div>
        <ToolTip
          label="pyramidLabel.hintTitle"
          tooltipInfo={formatHTMLMessageHelper(intl, 'statusMessageIS.MATCHES_PYRAMID.PASS.single', enrichedStats)}
          isTooltipInfoFormatted={true}
          btnModifiers={['pyramid']}
        />
      </div>
    </div>
  );
};

const HOCStageChooser = (Component: any) =>
  class extends React.Component<HOCStageChooserProps & HOCStageChooserStateProps & InjectedIntlProps> {
    state = {
      typeStage: this.getInitialtypeStage(),
    };

    private getInitialtypeStage() {
      if (this.props.isTestPeriodControlShown) {
        return this.props.isTestPeriodShown ? 'test' : 'train';
      }

      if (this.props.currentStage) {
        return this.props.currentStage.toLowerCase();
      }
      return 'is';
    }

    UNSAFE_componentWillReceiveProps(nextProps: any) {
      if (this.props.alphaId !== nextProps.alphaId && nextProps.currentStage) {
        this.setState({ typeStage: this.getInitialtypeStage() });
      }

      if (this.props.isTestPeriodShown !== nextProps.isTestPeriodShown) {
        if (nextProps.isTestPeriodShown === true) {
          this.setState({ typeStage: 'test' });
        } else {
          this.setState({ typeStage: 'train' });
        }
      }
    }

    UNSAFE_componentWillMount() {
      if (this.props.isTestPeriodControlShown === true) {
        if (this.props.isTestPeriodShown) {
          this.setState({ typeStage: 'test' });
        } else {
          this.setState({ typeStage: 'train' });
        }
      }
    }

    tabsStage() {
      if (this.props.currentStage === 'PROD') {
        return {
          is: { label: 'IS', status: this.props.alphaIs ? 'available' : 'unavailable' },
          os: { label: 'OS', status: this.props.alphaOs ? 'available' : 'unavailable' },
          prod: { label: 'Prod', status: this.props.alphaProd ? 'available' : 'unavailable' },
        };
      } else if (this.props.isTestPeriodControlShown && !this.props.isTestPeriodShown) {
        return {
          train: { label: 'TRAIN', status: 'available' },
          test: { label: 'TEST', status: 'unavailable' },
          is: { label: 'IS', status: 'unavailable' },
          os: { label: 'OS', status: 'unavailable' },
        };
      } else if (this.props.isTestPeriodControlShown && this.props.isTestPeriodShown) {
        return {
          train: { label: 'TRAIN', status: 'available' },
          test: { label: 'TEST', status: 'available' },
          is: { label: 'IS', status: 'available' },
          os: { label: 'OS', status: 'unavailable' },
        };
      } else {
        return {
          is: { label: 'IS', status: this.props.alphaIs ? 'available' : 'unavailable' },
          os: { label: 'OS', status: this.props.alphaOs ? 'available' : 'unavailable' },
        };
      }
    }

    handleChangeTabStage = (typeStage: TypeStage) => {
      if (this.props.currentStage !== null) {
        analytics.trackCustomEvent('sim_click_is_summary_stage', { event_label: typeStage });
        this.setState({ typeStage });
      }
    };

    handler = (item: TypeStage) => () => this.handleChangeTabStage(item);

    render() {
      const tabsStageAlphas: any = this.tabsStage();
      const typeStage = this.state?.typeStage;
      const props = this.props;
      const { modifiers = [], title = '', alphaId } = props;
      if (alphaId) {
        // @ts-ignore
        const themes: Theme[] = props.themes ? props.themes.toJS() : [];
        const classifications: Classification[] = props.classifications ? (props.classifications.toJS() as Classification[]) : [];
        const pyramidThemes = props.pyramidThemes ? (props.pyramidThemes.toJS() as PyramidThemes) : { effective: 0, pyramids: [] };
        const pyramids = pyramidThemes?.pyramids ?? [];

        const label = tabsStageAlphas[typeStage === 'test' || typeStage === 'train' ? 'is' : typeStage].label;
        return (
          <div id={props.id} className={addModifiers('alphas-details-content__box', modifiers)}>
            <div className={addModifiers('intro-step-8', modifiers)}>
              <div className="summary__header">
                <div className="summary__title">{`${label} ${title}`}</div>
                {!props.isStageChooserHidden && (
                  <ul className="summary__stage">
                    <li className="summary__stage-label">
                      <FormattedMessageHelper id="alpha.period" />
                    </li>
                    {Object.keys(tabsStageAlphas).map((item: any, index: number) => (
                      <li key={`${item}-${index}`} className="summary__stage-item">
                        <button
                          onClick={this.handler(item)}
                          className={addModifiers(
                            'summary__stage-item-btn',
                            item === typeStage ? [tabsStageAlphas[item].status, 'active'] : [tabsStageAlphas[item].status]
                          )}
                        >
                          {tabsStageAlphas[item].label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="summary__status-container">
                {props.renderGrade && <Status gradeOptions={props.gradeOptions} grade={props.alphaGrade} />}
                {props.renderTheme &&
                  themes.length > 0 &&
                  themes.map(theme => <AlphaStatusThemeLabel key={theme.id} theme={theme} intl={props.intl} />)}
                {props.renderClassification &&
                  classifications.length > 0 &&
                  classifications.map(classification => (
                    <AlphaStatusClassificationLabel key={classification.id} classification={classification} intl={props.intl} />
                  ))}
                {props.renderPyramids &&
                  pyramids.length > 0 &&
                  pyramids.map(pyramid => (
                    <AlphaStatusPyramidLabel key={pyramid.id} pyramid={pyramid} intl={props.intl} effective={pyramidThemes.effective} />
                  ))}
              </div>
              <Component {...props} typeStage={typeStage} tabsStageAlphas={tabsStageAlphas} />
            </div>
          </div>
        );
      } else {
        return null;
      }
    }
  };
const mapStateToProps = (state: StateType, ownProps: HOCStageChooserProps): HOCStageChooserStateProps => {
  const alphaPath = ownProps.path.concat(['alphaDetail']);
  return {
    // @ts-ignore
    currentStage: state.getIn(alphaPath.concat(['stage'])) !== 'PROD' ? state.getIn(alphaPath.concat(['stage'])) : 'OS',
    // @ts-ignore
    alphaId: state.getIn(alphaPath.concat(['id'])),
    // @ts-ignore
    alphaIs: state.getIn(alphaPath.concat(['is'])),
    // @ts-ignore
    alphaOs: state.getIn(alphaPath.concat(['os'])),
    // @ts-ignore
    alphaProd: state.getIn(alphaPath.concat(['prod'])),
    // @ts-ignore
    permissions: getUserPermissionsSelector(state),
    // @ts-ignore
    alphaGrade: state.getIn(alphaPath.concat(['grade'])),
    // @ts-ignore
    themes: state.getIn(alphaPath.concat(['themes'])),
    classifications: state.getIn(alphaPath.concat(['classifications'])) as Immutable.List<Classification> | undefined,
    pyramidThemes: state.getIn(alphaPath.concat(['pyramidThemes'])) as Immutable.Record<PyramidThemes> | undefined,
    // @ts-ignore
    fetchingCheck: state.getIn(ownProps.path.concat(['fetchingCheck'])),
    // @ts-ignore
    fetchingSubmissionAlpha: state.getIn(ownProps.path.concat(['fetchingSubmissionAlpha'])),
    alpha: state.getIn(alphaPath),
    // @ts-ignore
    userId: getUserIdFromUserSelector(state),
    // @ts-ignore
    gradeOptions: alphasOptionsGradeChoicesSelector(state),
    isTestPeriodShown: isTestPeriodShownSelector(state, ownProps),
    isTestPeriodControlShown: isTestPeriodControlShownSelector(state, ownProps),
  };
};

export default compose(injectIntl, connect<HOCStageChooserStateProps, any, any, StateType>(mapStateToProps, undefined), HOCStageChooser);
